import type {LabelPickerLabel$data, LabelPickerLabel$key} from '@github-ui/item-picker/LabelPickerLabel.graphql'
import {
  LabelFragment,
  LabelPickerBase,
  LazyLabelPicker,
  LazyLabelPickerOnClick,
} from '@github-ui/item-picker/LabelPicker'
import type {ItemPickerLabelsProps} from '@github-ui/item-picker2/ItemPickerLabels'
import type {SubmittedLabel} from '@github-ui/item-picker2/ItemPickerLabelsTypes'
import {ItemPickerLabels} from '@github-ui/item-picker2/ItemPickerLabels'
import {useCallback, useMemo, Suspense, type HTMLAttributes, useRef, useState} from 'react'
import {readInlineData} from 'react-relay'
import {graphql, useFragment, usePaginationFragment, useRelayEnvironment, type PreloadedQuery} from 'react-relay/hooks'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {LabelsList as Labels, type Label} from '@github-ui/labels-list'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {SectionHeader} from '@github-ui/issue-metadata/SectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import type {LabelsSectionAssignedLabelsQuery} from './__generated__/LabelsSectionAssignedLabelsQuery.graphql'
import LabelsSectionAssignedLabelsFragment, {
  type LabelsSectionAssignedLabels$key,
} from './__generated__/LabelsSectionAssignedLabels.graphql'
import type {LabelsSectionFragment$key} from './__generated__/LabelsSectionFragment.graphql'
import type {SxProp} from '@primer/react'
import type {LabelPickerQuery} from '@github-ui/item-picker/LabelPickerQuery.graphql'
import {commitUpdateIssueLabelsMutation2} from '@github-ui/item-picker/updateIssueLabelsMutation'
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ERRORS} from '@github-ui/item-picker/Errors'

type SelectedLabels = Exclude<ItemPickerLabelsProps['selectedLabelsKey'], undefined>

const ReadonlyLabelsSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.labels} />
const LabelsSectionHeader = (props: HTMLAttributes<HTMLElement>) => (
  <SectionHeader title={LABELS.sectionTitles.labels} buttonProps={props} />
)

type LabelsSectionProps = {
  sectionHeader: JSX.Element
  labels: Label[]
} & SxProp
const LabelsSection = ({sectionHeader, labels}: LabelsSectionProps) => {
  return (
    <Section sectionHeader={sectionHeader} emptyText={labels.length > 0 ? undefined : LABELS.emptySections.labels}>
      <Labels labels={labels} testId={TEST_IDS.issueLabels} />
    </Section>
  )
}

type CreateIssueLabelsSectionProps = {
  readonly: boolean
  labels: LabelPickerLabel$data[]
  labelQueryRef: PreloadedQuery<LabelPickerQuery, Record<string, unknown>>
  onSelectionChange: (labels: LabelPickerLabel$data[]) => void
  shortcutEnabled: boolean
}
export const CreateIssueLabelsSection = ({
  readonly,
  labels,
  labelQueryRef,
  onSelectionChange,
  ...sharedConfigProps
}: CreateIssueLabelsSectionProps) => {
  const sectionHeader = readonly ? (
    <ReadonlyLabelsSectionHeader />
  ) : (
    <LabelPickerBase
      readonly={readonly}
      labels={labels}
      labelQueryRef={labelQueryRef}
      onSelectionChange={onSelectionChange}
      anchorElement={LabelsSectionHeader}
      {...sharedConfigProps}
    />
  )
  return <LabelsSection sectionHeader={sectionHeader} labels={labels} />
}

graphql`
  fragment LabelsSectionAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 20})
  @refetchable(queryName: "LabelsSectionAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor) @connection(key: "MetadataSectionAssignedLabels_labels") {
      edges {
        node {
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...LabelPickerLabel
        }
      }
    }
  }
`

const labelsSectionFragment = graphql`
  fragment LabelsSectionFragment on Issue {
    id
    repository {
      name
      owner {
        login
      }
      isArchived
    }
    ...LabelsSectionAssignedLabels
    ...ItemPickerLabels_SelectedLabelsFragment
    # eslint-disable-next-line relay/unused-fields
    viewerCanUpdateNext
    viewerCanLabel
  }
`

export type EditIssueLabelsSectionProps = {
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  prefetchInteractionDataEnabled: boolean
  issuesReactViewerPickersEnabled: boolean
  issue: LabelsSectionFragment$key
}

export function EditIssueLabelsSection({
  issue,
  onIssueUpdate,
  singleKeyShortcutsEnabled,
  prefetchInteractionDataEnabled,
  issuesReactViewerPickersEnabled = false,
}: EditIssueLabelsSectionProps) {
  const data = useFragment(labelsSectionFragment, issue)
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
  } = data
  const {data: labelDataRaw} = usePaginationFragment<LabelsSectionAssignedLabelsQuery, LabelsSectionAssignedLabels$key>(
    LabelsSectionAssignedLabelsFragment,
    data,
  )

  const labelData = labelDataRaw

  const {id: issueId, viewerCanLabel} = data

  const labels = useMemo(
    () =>
      labelData.labels?.edges?.flatMap(e =>
        // eslint-disable-next-line no-restricted-syntax
        e?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, e?.node)] : [],
      ) || [],
    [labelData],
  )

  const readonlyLabel = !viewerCanLabel || isRepositoryArchived

  const labelSectionHeader = useMemo(() => {
    if (readonlyLabel) {
      return <ReadonlyLabelsSectionHeader />
    }

    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlyLabelsSectionHeader />}>
        <LazyLabelPicker
          labelableId={issueId}
          repo={repo}
          owner={owner}
          labelableTypeName={'Issue'}
          readonly={readonlyLabel}
          shortcutEnabled={singleKeyShortcutsEnabled}
          labels={labels}
          onIssueUpdate={onIssueUpdate}
          anchorElement={LabelsSectionHeader}
        />
      </Suspense>
    ) : (
      <LazyLabelPickerOnClick
        labelableId={issueId}
        repo={repo}
        owner={owner}
        labelableTypeName={'Issue'}
        readonly={readonlyLabel}
        shortcutEnabled={singleKeyShortcutsEnabled}
        labels={labels}
        onIssueUpdate={onIssueUpdate}
        anchorElement={LabelsSectionHeader}
      />
    )
  }, [
    readonlyLabel,
    prefetchInteractionDataEnabled,
    issueId,
    repo,
    owner,
    singleKeyShortcutsEnabled,
    labels,
    onIssueUpdate,
  ])

  return (
    <LabelsSection
      sectionHeader={
        issuesReactViewerPickersEnabled && !readonlyLabel ? (
          <SectionHeaderNew issueId={data.id} owner={owner} repo={repo} labels={data} onIssueUpdate={onIssueUpdate} />
        ) : (
          labelSectionHeader
        )
      }
      labels={labels}
    />
  )
}

const SectionHeaderNew = ({
  owner,
  repo,
  issueId,
  labels,
  onIssueUpdate,
}: {
  owner: string
  repo: string
  issueId: string
  labels: SelectedLabels
  onIssueUpdate?: () => void
}) => {
  const labelPickerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const onSelectionChange = useCallback(
    (selectedLabels: SubmittedLabel[]) => {
      commitUpdateIssueLabelsMutation2({
        environment,
        input: {issueId, labels: selectedLabels},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLabels,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate],
  )

  return (
    <>
      <SectionHeader
        title={LABELS.sectionTitles.labels}
        buttonProps={{onClick: () => setOpen(!open), ref: labelPickerRef}}
      />
      <ItemPickerLabels
        selectedLabelsKey={labels}
        title={'Select an item'}
        owner={owner}
        repo={repo}
        open={open}
        setOpen={setOpen}
        labelsButtonRef={labelPickerRef}
        onSubmit={onSelectionChange}
      />
    </>
  )
}

try{ ReadonlyLabelsSectionHeader.displayName ||= 'ReadonlyLabelsSectionHeader' } catch {}
try{ LabelsSectionHeader.displayName ||= 'LabelsSectionHeader' } catch {}
try{ LabelsSection.displayName ||= 'LabelsSection' } catch {}
try{ CreateIssueLabelsSection.displayName ||= 'CreateIssueLabelsSection' } catch {}
try{ EditIssueLabelsSection.displayName ||= 'EditIssueLabelsSection' } catch {}
try{ SectionHeaderNew.displayName ||= 'SectionHeaderNew' } catch {}